import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  Trash2,
  Filter,
  Search,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  ArrowDownCircle,
  ArrowUpCircle,
  Shield,
  Users,
  FileText,
  Zap,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  RefreshCw
} from 'lucide-react';
import { AdminNotificationsAPI } from '../services/api';

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 20
  });
  const [filters, setFilters] = useState({
    type: '',
    read_status: '',
    search: ''
  });
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch notifications
  const fetchNotifications = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: pagination.per_page,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === '') {
          delete params[key];
        }
      });
      
      const response = await AdminNotificationsAPI.getNotifications(params);
      
      setNotifications(response.data || []);
      setPagination(response.pagination || {
        current_page: 1,
        total_pages: 1,
        total_records: 0,
        per_page: 20
      });
    } catch (err) {
      // console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.per_page]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    // Reset to page 1 when filters change
    fetchNotifications(1);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchNotifications(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchNotifications(newPage);
    }
  };

  // Mark as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await AdminNotificationsAPI.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read_status: 1 } : n
        )
      );
    } catch (err) {
      // console.error('Failed to mark as read:', err);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await AdminNotificationsAPI.markAllAsRead();
      
      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, read_status: 1 }))
      );
    } catch (err) {
      // console.error('Failed to mark all as read:', err);
    }
  };

  // Delete notification
  const handleDelete = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }
    
    try {
      await AdminNotificationsAPI.deleteNotification(notificationId);
      
      // Update local state
      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );
      
      // Update pagination total
      setPagination(prev => ({
        ...prev,
        total_records: prev.total_records - 1
      }));
    } catch (err) {
      // console.error('Failed to delete notification:', err);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedNotifications.length === 0) {
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedNotifications.length} notification(s)?`)) {
      return;
    }
    
    try {
      await Promise.all(
        selectedNotifications.map(id => 
          AdminNotificationsAPI.deleteNotification(id)
        )
      );
      
      // Update local state
      setNotifications(prev =>
        prev.filter(n => !selectedNotifications.includes(n.id))
      );
      
      // Reset selection
      setSelectedNotifications([]);
      setSelectAll(false);
      
      // Update pagination total
      setPagination(prev => ({
        ...prev,
        total_records: prev.total_records - selectedNotifications.length
      }));
    } catch (err) {
      // console.error('Failed to delete notifications:', err);
    }
  };

  // Get notification icon
  const getNotificationIcon = (type, priority) => {
    const color = priority === 'high' ? 'text-red-500' :
                  priority === 'medium' ? 'text-yellow-500' : 'text-blue-500';
    
    const iconMap = {
      'deposit': <ArrowDownCircle className={`w-5 h-5 ${color}`} />,
      'withdrawal': <ArrowUpCircle className={`w-5 h-5 ${color}`} />,
      'kyc': <Shield className={`w-5 h-5 ${color}`} />,
      'user': <Users className={`w-5 h-5 ${color}`} />,
      'deposit_proof': <FileText className={`w-5 h-5 ${color}`} />,
      'system': <Zap className={`w-5 h-5 ${color}`} />
    };
    
    return iconMap[type] || <Bell className={`w-5 h-5 ${color}`} />;
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const colors = {
      'high': 'bg-red-500/10 text-red-500 border-red-500/20',
      'medium': 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      'low': 'bg-blue-500/10 text-blue-500 border-blue-500/20'
    };
    
    const icons = {
      'high': <AlertCircle className="w-3 h-3" />,
      'medium': <Info className="w-3 h-3" />,
      'low': <CheckCircle className="w-3 h-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colors[priority] || colors.low}`}>
        {icons[priority]}
        {priority}
      </span>
    );
  };

  // Get status badge
  const getStatusBadge = (readStatus) => {
    if (readStatus) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-600/20 text-gray-400 border border-gray-600/20">
          <EyeOff className="w-3 h-3" />
          Read
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/20 animate-pulse">
        <Eye className="w-3 h-3" />
        Unread
      </span>
    );
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications.map(n => n.id));
    }
    setSelectAll(!selectAll);
  };

  // Toggle select single
  const toggleSelect = (id) => {
    setSelectedNotifications(prev =>
      prev.includes(id)
        ? prev.filter(n => n !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Bell className="w-7 h-7 text-sky-400" />
              Notifications
            </h1>
            <p className="text-charcoal-gray text-sm mt-1">
              Manage all system notifications
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchNotifications(pagination.current_page)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Mark All Read
            </button>
            {selectedNotifications.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected ({selectedNotifications.length})
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors ${
                showFilters ? 'bg-sky-500/20 text-sky-400' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-charcoal-gray mb-1">Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                >
                  <option value="">All Types</option>
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdrawal</option>
                  <option value="kyc">KYC</option>
                  <option value="user">User</option>
                  <option value="deposit_proof">Deposit Proof</option>
                  <option value="system">System</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-charcoal-gray mb-1">Status</label>
                <select
                  value={filters.read_status}
                  onChange={(e) => handleFilterChange('read_status', e.target.value)}
                  className="w-full bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                >
                  <option value="">All Status</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-charcoal-gray mb-1">Search</label>
                <form onSubmit={handleSearch} className="flex gap-2">
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    placeholder="Search notifications..."
                    className="flex-1 bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-colors"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Notifications List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-gray-700 border-t-sky-500 rounded-full animate-spin mx-auto"></div>
              <p className="text-charcoal-gray mt-4">Loading notifications...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-400">{error}</p>
            <button
              onClick={() => fetchNotifications(1)}
              className="mt-3 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors text-sm"
            >
              Try Again
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-12 text-center">
            <Bell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No notifications</h3>
            <p className="text-charcoal-gray">All clear! No notifications to display.</p>
          </div>
        ) : (
          <>
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50 border-b border-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-sky-500 focus:ring-sky-500 focus:ring-offset-0"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">
                        Notification
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-charcoal-gray uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {notifications.map((notification) => (
                      <tr
                        key={notification.id}
                        className={`hover:bg-gray-800/30 transition-colors ${
                          !notification.read_status ? 'bg-sky-500/5' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedNotifications.includes(notification.id)}
                            onChange={() => toggleSelect(notification.id)}
                            className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-sky-500 focus:ring-sky-500 focus:ring-offset-0"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              {getNotificationIcon(notification.type, notification.priority)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-white">
                                  {notification.title}
                                </p>
                                {!notification.read_status && (
                                  <span className="w-2 h-2 bg-sky-500 rounded-full animate-pulse"></span>
                                )}
                              </div>
                              <p className="text-sm text-gray-400 mt-0.5 line-clamp-2">
                                {notification.message}
                              </p>
                              {notification.link && (
                                <a
                                  href={notification.link}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(notification.link);
                                  }}
                                  className="text-xs text-sky-400 hover:text-sky-300 mt-1 inline-block"
                                >
                                  View details →
                                </a>
                              )}
                              {notification.data && (
                                <div className="mt-1 text-xs text-charcoal-gray">
                                  <code className="bg-gray-800/50 px-2 py-0.5 rounded">
                                    {JSON.stringify(notification.data, null, 2)}
                                  </code>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-300 capitalize">
                            {notification.type?.replace('_', ' ') || 'System'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {getPriorityBadge(notification.priority)}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(notification.read_status)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-sm text-charcoal-gray">
                            <Clock className="w-3.5 h-3.5" />
                            {formatTime(notification.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {!notification.read_status && (
                              <button
                                onClick={() => handleMarkAsRead(notification.id)}
                                className="p-1.5 text-gray-400 hover:text-sky-400 hover:bg-sky-500/10 rounded transition-colors"
                                title="Mark as read"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(notification.id)}
                              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-charcoal-gray">
                  Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_records)} of{' '}
                  {pagination.total_records} notifications
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                      let pageNum;
                      if (pagination.total_pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.current_page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.current_page >= pagination.total_pages - 2) {
                        pageNum = pagination.total_pages - 4 + i;
                      } else {
                        pageNum = pagination.current_page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                            pageNum === pagination.current_page
                              ? 'bg-sky-500 text-white'
                              : 'text-gray-400 hover:text-white hover:bg-gray-800'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {pagination.total_pages > 5 && pagination.current_page < pagination.total_pages - 2 && (
                      <>
                        <span className="text-charcoal-gray">...</span>
                        <button
                          onClick={() => handlePageChange(pagination.total_pages)}
                          className="w-9 h-9 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                        >
                          {pagination.total_pages}
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.total_pages}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Notifications;